import { LoadingOutlined } from '@ant-design/icons';
import Images from 'app/assets/img';
import { ReactComponent as ArrowDownIcon } from 'app/assets/img/arrow_down.svg';
import { ReactComponent as CalendarIcon } from 'app/assets/img/calendar-icon.svg';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { StyledStack } from 'app/components/Stack';
import useFetchData from 'app/hooks/useFetchData';
import useWindowSize from 'app/hooks/useWindowSize';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import {
  getAvailableBookingSlotApi,
  getParkListApi,
  getRestaurantListApi,
  getTicketShowListApi,
  getZoneListApi,
} from 'app/services/bookingSlotApi';
import _ from 'lodash';
import moment from 'moment';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { objectType } from 'types';
import { getDayOfWeekString } from 'utils/time';
import { convertVItoEN } from '../../CheckTickets/helper';
import StyledDatePicker, {
  DatePickerWrapper,
} from '../../components/StyledDatePicker';
import StyledSelect from '../../components/StyledSelect';
import BookingModal from './BookingModal';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import TooltipParagraph from 'app/pages/Catalog/tooltipParagraph';
export const formatDate = (
  date: moment.Moment,
  translation: objectType | undefined,
) => {
  const dayOfWeek = getDayOfWeekString(date, translation);
  // Format the rest of the date
  return `${dayOfWeek} ${date.format('(DD/MM/YYYY)')}`;
};
export type IFilterBookingSlotSearch = {
  parkId?: number | string;
  orderDate?: string | moment.Moment;
  product_id?: string | number;
  storeId?: string | number;
};
export const DEFAULT_FILTER_SEARCH: IFilterBookingSlotSearch = {
  parkId: undefined,
  orderDate: undefined,
  product_id: undefined,
  storeId: undefined,
};
type IDataSelect = {
  value: string;
  label: string;
  item?: any;
};
type IDataZone = {
  id: number;
  name: string;
  slot: number;
};
export interface IDataSlot {
  id?: number;
  ticketName?: string;
  totalAvailableSlot?: number;
  numberOfUsed?: number;
  dataZone?: IDataZone[];
  orderDate?: string | moment.Moment;
  productId?: string | number;
  storeId?: string | number;
  quantity?: number;
  zone?: string;
  infantQuantity?: number;
  customerNationality?: string;
  note?: string;
  fullName?: string;
  phoneNumber?: string;
  arrivalDate?: string | moment.Moment;
  referenceCode?: string;
  reservationCode?: string;
}
const CheckBookingSlots = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { device } = useWindowSize();
  const [filter, setFilter] = useState<IFilterBookingSlotSearch>(
    DEFAULT_FILTER_SEARCH,
  );
  const [filterItemWith, setFilterItemWidth] = useState({
    small: 0,
    normal: 0,
  });
  const [dataProduct, setDataProduct] = useState<IDataSelect[]>([]);
  const [dataRestaurant, setDataRestaurant] = useState<IDataSelect[]>([]);
  const [dataZone, setDataZone] = useState<any>([]);
  const [isOpenBookingModal, setIsOpenBookingModal] = useState<boolean>(false);
  const [dataSlot, setDataSlot] = useState<IDataSlot | null | undefined>(
    undefined,
  );
  const [isChecking, setIsChecking] = useState(false);
  const translations = useSelector(selectTranslations);
  const { locale } = useGlobalFunction();
  useEffect(() => {
    onFetchParkList({ payloadData: { workstationId: 'HTI' } });
  }, []);
  const { result: listPark, onFetchData: onFetchParkList } = useFetchData({
    actionApi: getParkListApi,
    IIFE: false,
  });
  const {
    onFetchData: onFetchRestaurantList,
    isLoading: isLoadingRestaurantList,
  } = useFetchData({
    actionApi: getRestaurantListApi,
    IIFE: false,
    onSuccess: response => {
      if (_.isEmpty(response)) return [];
      const resData = (response || []).map((item: any) => {
        return {
          label: item.name,
          value: `${item.storeId}`,
          item: item,
        };
      });
      setDataRestaurant(resData);
    },
  });
  const { onFetchData: onFetchProductList, isLoading: isLoadingProductList } =
    useFetchData({
      actionApi: getTicketShowListApi,
      IIFE: false,
      onSuccess: response => {
        if (_.isEmpty(response)) return [];
        const resData = (response || []).map((item: any) => {
          return {
            label: item.name,
            value: `${item.id}`,
          };
        });
        setDataProduct(resData);
      },
    });
  const { onFetchData: onFetchAvailableBookingSlot } = useFetchData({
    actionApi: getAvailableBookingSlotApi,
    IIFE: false,
  });
  const { onFetchData: onFetchZoneList, isLoading: isLoadingZoneList } =
    useFetchData({
      actionApi: getZoneListApi,
      IIFE: false,
      onSuccess: response => {
        if (_.isEmpty(response)) {
          setDataSlot(null);
        } else {
          setDataZone(response);
        }
      },
    });
  const travelLocation = useMemo(() => {
    return [
      ...(listPark ?? [])?.map(item => ({
        value: `${item.id}`,
        label: item.name,
      })),
    ];
  }, [listPark]);
  useLayoutEffect(() => {
    const getFilterItemWidth = () => {
      if (!containerRef.current) return;
      const containerWidth = Math.floor(
        containerRef.current!.getBoundingClientRect().width,
      );
      setFilterItemWidth({
        small: (containerWidth! - 5 * 12 - 120) / 5,
        normal: (containerWidth! - 3 * 12) / 4,
      });
    };

    getFilterItemWidth();
    window.addEventListener('resize', getFilterItemWidth);
    return () => {
      window.removeEventListener('resize', getFilterItemWidth);
    };
  }, []);
  const onChangeDate = (date: moment.Moment | null) => {
    if (date) {
      setFilter({ ...filter, orderDate: date.format('YYYY-MM-DD') });
    } else {
      setFilter({ ...filter, orderDate: '' });
    }
  };

  const onChangePark = val => {
    setFilter({
      ...filter,
      parkId: val,
      storeId: undefined,
      product_id: undefined,
    });
    setDataRestaurant([]);
    setDataProduct([]);
    setDataZone([]);
    if (val) {
      onFetchRestaurantList({ payloadData: { parkId: val } });
    }
  };
  const onChangeRestaurant = (val, option) => {
    setFilter({ ...filter, storeId: val, product_id: undefined });
    setDataProduct([]);
    setDataZone([]);
    if (val) {
      onFetchProductList({
        payloadData: {
          restaurantId: option?.item?.restaurant?.id,
          parkId: filter.parkId,
        },
      });
      onFetchZoneList({ payloadData: { storeId: val } });
    }
  };
  const onChangeProduct = val => {
    setFilter({ ...filter, product_id: val });
  };
  const handleMergeData = (responses: any[]) => {
    if (_.isEmpty(responses)) {
      setDataSlot(null);
      return;
    }
    const dataZoneTemp = dataZone.map((item: any) => {
      const matched = responses.find(
        res => String(res?.zoneId || res?.zoneID) === String(item.zoneId),
      );
      return {
        id: item.zoneId,
        name: `Zone ${item?.zoneId} (${
          locale === 'vi' ? item.zoneNameVn : item.zoneNameEn
        })`,
        slot: matched?.numberOfUnused || 0,
      };
    });
    const totalAvailableSlot = dataZoneTemp.reduce(
      (acc, cur) => acc + cur.slot,
      0,
    );
    const totalUsed = responses.reduce(
      (acc, cur) => acc + (cur?.numberOfUsed || 0),
      0,
    );
    const ticketName =
      dataProduct?.find(el => el.value === filter.product_id)?.label || '';
    const dataTemp = {
      ticketName,
      totalAvailableSlot,
      numberOfUsed: totalUsed,
      dataZone: dataZoneTemp,
      orderDate: filter.orderDate,
      productId: filter.product_id,
      storeId: filter.storeId,
    };
    setDataSlot(dataTemp);
  };
  const onClickCheck = async () => {
    if (_.isEmpty(dataZone)) return;
    setIsChecking(true);
    try {
      const responses: any[] = [];
      for (const zone of dataZone) {
        const params = {
          zoneId: zone.zoneId,
          arrivalDate: filter.orderDate,
        };
        const res = await onFetchAvailableBookingSlot({
          payloadData: params,
        });
        if (res) {
          responses.push(res);
        }
      }
      handleMergeData(responses);
    } catch (err) {
      console.error(err);
    } finally {
      setIsChecking(false);
    }
  };
  const onClickBooking = () => {
    setIsOpenBookingModal(true);
  };
  const onCloseBookingModal = (isCallApi?: boolean) => {
    setIsOpenBookingModal(false);
    if (isCallApi) {
      onClickCheck();
    }
  };
  return (
    <>
      <LoadingContainer loading={isChecking}>
        <Title>{translations?.['b2b.txt.bookingLookup']}</Title>
        <Description>
          {translations?.['b2b.txt.sunWorldRestaurantTicketNote']}
        </Description>
        <StyledStack
          columnGap={'12px'}
          direction={{ lg: 'row', md: 'column' }}
          style={{ width: '100%', flexWrap: 'wrap' }}
          rowGap={'8px'}
          ref={containerRef}
        >
          <StyledStack
            style={{
              width: device === 'desktop' ? filterItemWith.normal : '100%',
            }}
          >
            <StyledSelect
              filterOption={(input, option) =>
                (convertVItoEN(option?.label) ?? '').includes(
                  convertVItoEN(input),
                )
              }
              showSearch
              isFocus
              value={filter.parkId}
              suffixIcon={<ArrowDownIcon />}
              placeholder={
                translations ? `${translations['b2c.txt.park']}*` : ''
              }
              onChange={val => onChangePark(val)}
              options={travelLocation}
              style={{ width: '100%' }}
              allowClear
            />
          </StyledStack>
          <StyledStack
            style={{
              width: device === 'desktop' ? filterItemWith.normal : '100%',
            }}
          >
            <StyledSelect
              filterOption={(input, option) =>
                (convertVItoEN(option?.label) ?? '').includes(
                  convertVItoEN(input),
                )
              }
              showSearch
              isFocus
              value={filter.storeId}
              suffixIcon={
                isLoadingRestaurantList ? (
                  <LoadingOutlined
                    style={{
                      fontSize: 20,
                      color: '#1890ff',
                    }}
                  />
                ) : (
                  <ArrowDownIcon />
                )
              }
              placeholder={
                translations ? `${translations['b2b.txt.restaurant']}*` : ''
              }
              onChange={(value, option) => onChangeRestaurant(value, option)}
              options={dataRestaurant}
              style={{ width: '100%' }}
              allowClear
              disabled={!filter.parkId || isLoadingRestaurantList}
              loading={isLoadingRestaurantList}
            />
          </StyledStack>
          <StyledStack
            style={{
              width: device === 'desktop' ? filterItemWith.normal : '100%',
            }}
          >
            <StyledSelect
              filterOption={(input, option) =>
                (convertVItoEN(option?.label) ?? '').includes(
                  convertVItoEN(input),
                )
              }
              showSearch
              isFocus
              value={filter.product_id}
              suffixIcon={
                isLoadingProductList || isLoadingZoneList ? (
                  <LoadingOutlined
                    style={{
                      fontSize: 20,
                      color: '#1890ff',
                    }}
                  />
                ) : (
                  <ArrowDownIcon />
                )
              }
              placeholder={
                translations ? `${translations['b2b.booking.products']}*` : ''
              }
              onChange={val => onChangeProduct(val)}
              options={dataProduct}
              style={{ width: '100%' }}
              allowClear
              disabled={
                !filter.parkId ||
                !filter.storeId ||
                isLoadingRestaurantList ||
                isLoadingProductList ||
                isLoadingZoneList ||
                _.isEmpty(dataZone)
              }
              loading={isLoadingProductList || isLoadingZoneList}
            />
          </StyledStack>
          <DatePickerWrapper
            style={{
              width: device === 'desktop' ? filterItemWith.normal : '100%',
            }}
          >
            <StyledDatePicker
              className="dataPicker"
              dropdownClassName="custom-date-popup"
              getPopupContainer={trigger => trigger.parentElement!}
              suffixIcon={<CalendarIcon />}
              placeholder={
                translations ? `${translations?.['b2b.txt.selectDay']}*` : ''
              }
              format={date => formatDate(date, translations)}
              allowClear
              disabledDate={current =>
                current && current < moment().startOf('day')
              }
              value={
                filter.orderDate && moment(filter.orderDate).isValid()
                  ? moment(filter.orderDate)
                  : null
              }
              onChange={onChangeDate}
              style={{
                width: device !== 'desktop' ? '100%' : 'auto',
              }}
            />
          </DatePickerWrapper>
          <CheckButton
            disabled={
              !filter.parkId ||
              !filter.orderDate ||
              !filter.product_id ||
              !filter.storeId
            }
            onClick={onClickCheck}
          >
            {translations?.['b2b.txt.check']}
          </CheckButton>
        </StyledStack>
        {(!filter.parkId ||
          !filter.orderDate ||
          !filter.product_id ||
          !filter.storeId) && (
          <NoteRequire>
            {translations?.['b2b.txt.pleaseFillRequiredFields']}
          </NoteRequire>
        )}
        {!_.isEmpty(dataSlot) && !isChecking && (
          <BoxContent>
            <CardTitle ellipsis={{ rows: 2 }}>{dataSlot?.ticketName}</CardTitle>
            <InfoBox>
              {translations?.['b2b.txt.preBookedSlots']}&nbsp;
              <b>{dataSlot?.numberOfUsed}</b>
            </InfoBox>
            <InfoBox>
              <div>
                <b>
                  {translations?.['b2b.txt.totalAvailableSlots']
                    .replace(
                      '${day}',
                      moment(dataSlot.orderDate, [
                        'YYYY-MM-DD',
                        'DD/MM/YYYY',
                      ]).format('DD/MM/YYYY'),
                    )
                    .replace('${n}', dataSlot?.totalAvailableSlot)}
                </b>
              </div>
              <BoxZone>
                {dataSlot?.dataZone?.map((zone, index) => {
                  return (
                    <div key={String(zone?.id || index)}>
                      {zone?.name}:&nbsp;<b>{zone.slot}</b>&nbsp;slots
                    </div>
                  );
                })}
              </BoxZone>
            </InfoBox>
            <BookingButton
              disabled={dataSlot?.totalAvailableSlot === 0}
              onClick={onClickBooking}
            >
              {translations?.['b2b.txt.makeReservation']}
            </BookingButton>
          </BoxContent>
        )}
        {dataSlot === undefined && (
          <BoxStart>
            <TextPleaseSearch>
              {translations?.['b2b.txt.pleaseSearchBookingInfo']}
            </TextPleaseSearch>
            <img src={Images.SunImg} alt="" width={153} height={37} />
          </BoxStart>
        )}
        {dataSlot === null && !isChecking && (
          <BoxNoData>
            <TextPleaseSearch>
              {translations?.['b2b.txt.noBookingInfoFound']}
            </TextPleaseSearch>
            <img src={Images.SunImg} alt="" width={153} height={37} />
          </BoxNoData>
        )}
      </LoadingContainer>
      {isOpenBookingModal && !_.isEmpty(dataSlot) && (
        <BookingModal
          isShow={isOpenBookingModal}
          data={dataSlot ?? null}
          onCloseModal={onCloseBookingModal}
          typeModal="create"
          filter={filter}
        />
      )}
    </>
  );
};
export default CheckBookingSlots;
const Title = styled.div`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
`;
const Description = styled.div`
  font-size: 14px;
  margin-bottom: 14px;
`;
const CheckButton = styled.button`
  height: 42px;
  padding: 0 24px;
  border: none;
  // border: 1px solid var(--color-4);
  border-radius: 6px;
  background: var(--color-gradient);
  color: white;
  // background: white;
  // color: var(--color-4);
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    background: #d4d4d4;
    cursor: not-allowed;
    opacity: 1;
  }
`;
const NoteRequire = styled.div`
  font-size: 12px;
  color: var(--color-9);
  margin-top: 10px;
  font-style: italic;
`;
const TextPleaseSearch = styled.div``;
const BoxStart = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-top: 20px;
  img {
    margin-top: 20px;
  }
  background: white;
  border-radius: 8px;
  padding: 32px;
  border: 1px solid #eee;
`;
const BoxNoData = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-top: 20px;
  img {
    margin-top: 20px;
  }
  background: white;
  border-radius: 8px;
  padding: 32px;
  border: 1px solid #eee;
`;
const BoxContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 32px;
  border: 1px solid #eee;
  margin-top: 20px;
`;

const CardTitle = styled(TooltipParagraph)`
  font-size: 20px;
  line-height: 1.4;
  margin-bottom: 24px;
`;

const InfoBox = styled.div`
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
  font-size: 14px;
`;
const BoxZone = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 30px;
`;
const BookingButton = styled.button`
  display: block;
  margin: 32px auto 0;
  padding: 12px 40px;
  border-radius: 6px;
  border: none;
  background: var(--color-gradient);
  color: white;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    background: #d4d4d4;
    cursor: not-allowed;
    opacity: 1;
  }
`;
